import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import { IColourFormValues } from '../../../../../@types/model/masterData/colour/colourFormValues';
import { useAppSelector } from '../../../../../@types/redux';
import { PillButton, ControlledTextField, ControlledMuiAutoCompleteSelect } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import ColourModelHelper from '../../../../../@types/model/masterData/colour/colourModelHelper';
import { yupResolver } from '@hookform/resolvers/yup';
import OptionType from '../../../../../@types/model/optionType';
import CustomReactHookFormDevTools from '../../../../../components/devtools/ReactHookFormDevTools';

interface IColourFormProps {
    isLoading : boolean;
    initialValues : IColourFormValues;
    onSubmit : (values : IColourFormValues) => void;
    onClose : () => void;
}

const ColourForm = (props : IColourFormProps) : React.ReactElement => {
    const commodities = useAppSelector(x => x.masterData.commodities);

    const { handleSubmit, control, formState: { isValid, isDirty }, trigger } = useForm<IColourFormValues>({
        defaultValues: props.initialValues,
        resolver: yupResolver(ColourModelHelper.formSchema()),
        mode: 'onChange',
    });

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        trigger();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const commodityOptions = useMemo(() => {
        return commodities.filter(x => x.isActive)
            .map(OptionType.fromDataModel);
    }, [commodities]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div>
            <form onSubmit={handleSubmit(props.onSubmit)}>
                <div className={'fdc p20 pt15 pb10'}>
                    <ControlledTextField
                        className={'mb10'}
                        control={control}
                        label={'Code'}
                        name={'code'}
                        defaultValue={props.initialValues.code}
                        size={'small'}
                    />
                    <ControlledTextField
                        className={'mb10'}
                        control={control}
                        label={'Name'}
                        name={'name'}
                        defaultValue={props.initialValues.name}
                        size={'small'}
                    />
                    <ControlledMuiAutoCompleteSelect
                        className={'mb10'}
                        control={control}
                        label={'Commodities'}
                        defaultValues={props.initialValues.commodities}
                        options={commodityOptions}
                        isMulti
                        name={'commodities'}
                    />
                    <div className={'flx1 fdr jcfs'}>
                        <ControlledLabelledCheckBox 
                            name={'isActive'}
                            label={'Is Active?'}
                            control={control}
                            defaultValue={props.initialValues.isActive}
                            disabled={props.initialValues.isActive}
                        />
                    </div>
                    <div className={'fdr flx1 aic jcfe mb20'}>
                        <Button 
                            className={'cpd mr20'}
                            onClick={props.onClose} 
                            type={'reset'}>
                            CANCEL
                        </Button>
                        <PillButton
                            className={'pl30 pr30'}
                            text={'SAVE'}
                            size={'small'}
                            type={'submit'}
                            color={'secondary'}
                            disabled={ !isDirty || !isValid || props.isLoading }
                        />
                    </div>
                </div>
            </form>
            <CustomReactHookFormDevTools control={control} />
        </div>
    );
};

export default ColourForm;
